// Fiche d'un routeur MikroTik, rendue par DeviceDetail quand `platform === 'routeros'`.
//
// DeviceDetail charge déjà `/devices/{id}` (alertes actives, agent) et ses
// rafraîchissements périodiques (maintenance, commandes planifiées) : il les
// passe en props, pour ne pas doubler les sondages. Ce composant ajoute ce que
// seul `/platform/mikrotik/{id}` connaît (matériel, versions, interfaces…).

import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  Router,
  Power,
  Wrench,
  Trash2,
  SlidersHorizontal,
  RefreshCw,
  KeyRound,
  ArrowUpCircle,
  MonitorUp,
  AlertTriangle,
  Clock,
  Cpu,
  Network,
  Globe,
  Thermometer,
  Tag,
  ShieldCheck,
} from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useDashboard } from '../views/dashboardContext.jsx';
import { hasPermission } from '../lib/permissions.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { useRealtime, useCoalesced } from '../lib/realtime.jsx';
import { fmtOnlineStatus, fmtRelativeFromNow } from '../lib/format.jsx';
import { LaunchButton } from '../lib/launch.jsx';
import {
  UpdatePill,
  fmtBytes,
  fmtDateTime,
  mikrotikCall,
  mikrotikCommandLabel,
  pollProblem,
} from '../lib/mikrotik.jsx';
import { buildAlertTaskText, ComitBonChip, ComitTaskButton } from '../lib/comit.jsx';
import { ALERT_SEV_COLORS, ALERT_SEV_LABELS, ALERT_CAT_LABELS } from '../components/AlertsTable.jsx';
import { DeviceActivityPanel } from '../components/DeviceActivityPanel.jsx';
import { UptimeHeatmap } from '../components/UptimeHeatmap.jsx';
import { PerformanceHistory } from '../components/PerformanceHistory.jsx';
import { AlertThresholdsModal } from '../components/AlertThresholdsModal.jsx';
import { RestartModal } from '../components/RestartModal.jsx';
import { MaintenanceModal } from '../components/MaintenanceModal.jsx';
import { MikrotikDeviceModal } from '../components/MikrotikDeviceModal.jsx';
import { MikrotikUpgradeModal } from '../components/MikrotikUpgradeModal.jsx';
import { MikrotikBackupsPanel } from '../components/MikrotikBackupsPanel.jsx';

const sectionCls = 'bg-card border border-rule rounded-2xl px-5 py-4 mb-4';
const sTitleCls = 'text-sm font-bold text-fg-2 mb-3.5 flex items-center gap-2';
const tableCls = 'w-full border-collapse text-xs';
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule';
const tdCls = 'px-3 py-2 text-fg-2 border-b border-sidebar';
const btnCls =
  'bg-rule border border-rule-3 rounded-lg px-3.5 py-2 text-muted cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed';

const KV = ({ label, value, children }) => (
  <div className="text-xs">
    <div className="text-muted-2 text-[11px] mb-0.5">{label}</div>
    <div className="text-fg font-mono text-[12.5px] break-words">{children ?? value ?? '—'}</div>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className={sectionCls}>
    <div className={sTitleCls}>
      <Icon size={16} strokeWidth={1.75} /> {title}
    </div>
    {children}
  </div>
);

const Banner = ({ tone = 'warn', icon: Icon = AlertTriangle, children, actions }) => {
  const cls =
    tone === 'crit'
      ? 'bg-crit/10 border-crit/30'
      : tone === 'info'
        ? 'bg-blue-500/10 border-blue-500/30'
        : 'bg-warn/10 border-warn/30';
  const iconCls = tone === 'crit' ? 'text-crit' : tone === 'info' ? 'text-blue-400' : 'text-warn';
  return (
    <div className={`${cls} border rounded-xl px-4 py-2.5 mb-4 flex items-center justify-between gap-3 flex-wrap`}>
      <div className="text-[12.5px] text-fg-2 inline-flex items-start gap-2 flex-1 min-w-[260px]">
        <Icon size={14} strokeWidth={1.75} className={`${iconCls} flex-shrink-0 mt-0.5`} />
        <span>{children}</span>
      </div>
      {actions && <div className="flex gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
};

const bannerBtn =
  'bg-transparent border border-rule-3 rounded-lg px-3 py-1 text-fg-2 cursor-pointer text-[11px] font-semibold hover:bg-rule-2 disabled:opacity-60';

const pct = (used, total) => (total > 0 ? Math.round((used / total) * 100) : null);

export function RouterDetail({
  agentId,
  d,
  perms,
  reload,
  maintenance,
  loadMaintenance,
  scheduledCmds,
  loadScheduled,
}) {
  const { navigate } = useDashboard();
  // Retour à la liste des routeurs plutôt qu'au tableau de bord : on y arrive de là.
  const onBack = () => navigate('mikrotik');
  const canRemote = hasPermission(perms, 'RemoteConnect');
  const canRun = hasPermission(perms, 'RunCommands');
  const canInstall = hasPermission(perms, 'InstallUpdates');
  const canProvision = hasPermission(perms, 'ManageProvisioning');
  const canDelete = hasPermission(perms, 'DeleteDevices');
  const canManageConfig = hasPermission(perms, 'ManageConfig');
  const canManageAlerts = hasPermission(perms, 'ManageAlerts');
  const canViewClients = hasPermission(perms, 'ViewClients');

  const [mk, setMk] = useState(null);
  const [missing, setMissing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [repinning, setRepinning] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [modal, setModal] = useState(null); // 'edit' | 'upgrade' | 'restart' | 'maintenance' | 'thresholds'
  const [upgradeNotice, setUpgradeNotice] = useState(null);
  const ifaceSort = useTableSort('name');

  const load = useCallback(async () => {
    const r = await api.get('/platform/mikrotik/' + agentId);
    if (r) {
      setMk(r);
      setMissing(false);
    } else setMissing(true);
  }, [agentId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  // Les événements ne déclenchent que des relectures scopées (cf. realtime.jsx).
  const refreshAll = useCoalesced(() => {
    load();
    reload?.();
    loadScheduled?.();
  }, 2000);
  useRealtime('InventoryUpdated', refreshAll);
  useRealtime('AgentStatusChanged', refreshAll);
  useRealtime('CommandCompleted', refreshAll);
  useRealtime('CommandCreated', refreshAll);

  if (!mk) {
    return (
      <div className="w-full">
        <button onClick={onBack} className={`${btnCls} mb-4`}>
          <ArrowLeft size={14} strokeWidth={1.75} /> Retour
        </button>
        <div className={missing ? 'text-crit p-6' : 'text-muted-2 p-6'}>
          {missing ? 'Routeur introuvable ou accès refusé.' : 'Chargement...'}
        </div>
      </div>
    );
  }

  const a = d.agent;
  const name = mk.name || a.label || a.computerName;
  const problem = pollProblem(mk);
  const memUsedPct = mk.isOnline ? Math.round(mk.memoryPercent) : null;
  const diskUsedPct = pct(mk.totalHddBytes - mk.freeHddBytes, mk.totalHddBytes);
  const alerts = d.activeAlerts || [];

  const pollNow = async () => {
    setPolling(true);
    setActionError(null);
    const r = await mikrotikCall('/platform/mikrotik/' + agentId + '/poll', 'POST', {});
    setPolling(false);
    if (r.ok && r.json) {
      setMk(r.json);
      reload?.();
    } else setActionError(r.json?.error || 'La collecte a échoué.');
  };

  const repin = async () => {
    if (
      !confirm(
        `Accepter la nouvelle clé SSH de « ${name} » ?\n\nÀ ne faire que si le changement est attendu (routeur remplacé, réinitialisé, clé régénérée). Une clé inattendue peut signaler une interception de la connexion.`
      )
    )
      return;
    setRepinning(true);
    setActionError(null);
    const r = await mikrotikCall('/platform/mikrotik/' + agentId + '/reset-hostkey', 'POST', {});
    setRepinning(false);
    if (r.ok && r.json?.success !== false) load();
    else setActionError(r.json?.error || "Impossible d'accepter la nouvelle clé.");
    reload?.();
  };

  const remove = async () => {
    if (
      !confirm(
        `Supprimer définitivement le routeur "${name}" et toutes ses données (inventaire, alertes, historique, sauvegardes référencées) ?\n\nCette action est irréversible.`
      )
    )
      return;
    const ok = await api.del('/devices/' + agentId);
    if (!ok) {
      alert('Échec de la suppression — vérifier les permissions ou les logs serveur.');
      return;
    }
    onBack();
  };

  const cancelScheduled = async (id) => {
    const r = await mikrotikCall('/platform/commands/' + id + '/cancel', 'POST', {});
    if (!r.ok) setActionError(r.json?.error || "Échec de l'annulation.");
    loadScheduled?.();
    loadMaintenance?.();
  };

  const interfaces = ifaceSort.sortRows(mk.interfaces || [], (i, key) => {
    switch (key) {
      case 'rx':
        return i.rxBytes;
      case 'tx':
        return i.txBytes;
      case 'downs':
        return i.linkDowns;
      case 'state':
        return i.disabled ? 0 : i.running ? 2 : 1;
      default:
        return i[key];
    }
  });
  const { headerProps, arrow } = ifaceSort;

  const upgradeAvailable = mk.updateAvailable || mk.firmwareUpgradeAvailable;

  return (
    <div className="w-full">
      {/* ─── En-tête ─── */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <button onClick={onBack} className={btnCls}>
          <ArrowLeft size={14} strokeWidth={1.75} /> Retour
        </button>
        <div className="flex-1 min-w-[260px]">
          <h1 className="text-[22px] font-bold text-fg inline-flex items-center gap-2">
            <Router size={20} strokeWidth={1.75} className="text-blue-400" /> {name}
          </h1>
          <div className="text-xs text-muted-2 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-violet-500/15 text-violet-400">
              RouterOS
            </span>
            {[
              mk.clientName ? (
                canViewClients ? (
                  <button
                    key="client"
                    type="button"
                    onClick={() => navigate('clientDetail', { client: mk.clientId })}
                    title="Ouvrir la fiche du client"
                    className="bg-transparent border-none p-0 text-blue-400 hover:underline cursor-pointer text-xs font-semibold"
                  >
                    {mk.clientName}
                  </button>
                ) : (
                  <span key="client" className="text-fg-2 font-semibold">
                    {mk.clientName}
                  </span>
                )
              ) : null,
              mk.siteName ? <span key="site">{mk.siteName}</span> : null,
              mk.identity && mk.identity !== name ? <span key="id">Identité : {mk.identity}</span> : null,
              <span key="host" className="font-mono">
                {mk.host}
              </span>,
            ]
              .filter(Boolean)
              .map((node, i) => (
                <span key={node.key} className="inline-flex items-center gap-2">
                  {i > 0 && <span className="text-muted-3">·</span>}
                  {node}
                </span>
              ))}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${mk.isOnline ? 'bg-ok' : 'bg-crit'}`} />
          <span
            className={`text-xs font-semibold ${mk.isOnline ? 'text-ok' : 'text-crit'}`}
            title={!mk.isOnline && mk.lastHeartbeat ? `Dernier signal : ${fmtDateTime(mk.lastHeartbeat)}` : undefined}
          >
            {fmtOnlineStatus(mk.isOnline, mk.lastHeartbeat)}
          </span>
        </div>
      </div>

      {/* ─── Actions ─── */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {canRemote && (
          <LaunchButton
            path={'/platform/launch/winbox/' + agentId}
            icon={MonitorUp}
            title="Ouvrir WinBox sur ce routeur (via Fonxtio Tech)"
          >
            WinBox
          </LaunchButton>
        )}
        <button onClick={pollNow} disabled={polling} className={btnCls} title="Collecter les mesures maintenant">
          <RefreshCw size={14} strokeWidth={1.75} className={polling ? 'animate-spin' : ''} />
          {polling ? 'Collecte…' : 'Sonder maintenant'}
        </button>
        {canRun && (
          <button onClick={() => setModal('restart')} className={btnCls} title="Redémarrer le routeur maintenant ou à une heure planifiée">
            <Power size={14} strokeWidth={1.75} /> Redémarrer
          </button>
        )}
        {canInstall && (
          <button
            onClick={() => setModal('upgrade')}
            disabled={!upgradeAvailable}
            className={btnCls}
            title={upgradeAvailable ? 'Mettre à jour RouterOS / le firmware' : 'Aucune mise à jour disponible'}
          >
            <ArrowUpCircle size={14} strokeWidth={1.75} /> Mettre à jour
            {mk.updateAvailable && <span className="font-mono text-warn">{mk.latestVersion}</span>}
          </button>
        )}
        {canProvision && (
          <button onClick={() => setModal('edit')} className={btnCls}>
            <KeyRound size={14} strokeWidth={1.75} /> Modifier l'accès
          </button>
        )}
        <button onClick={() => setModal('thresholds')} className={btnCls} title="Configurer les seuils d'alertes pour ce routeur">
          <SlidersHorizontal size={14} strokeWidth={1.75} /> Seuils d'alertes
        </button>
        {canManageAlerts && !maintenance?.active && (
          <button onClick={() => setModal('maintenance')} className={btnCls} title="Suspendre temporairement les alertes et notifications">
            <Wrench size={14} strokeWidth={1.75} /> Maintenance
          </button>
        )}
        {canDelete && (
          <button
            onClick={remove}
            className="bg-crit/12 border border-crit/35 rounded-lg px-3.5 py-2 text-crit cursor-pointer text-xs font-semibold hover:bg-crit/20 inline-flex items-center gap-1.5"
          >
            <Trash2 size={14} strokeWidth={1.75} /> Supprimer
          </button>
        )}
      </div>

      {/* ─── Bannières ─── */}
      {actionError && (
        <Banner tone="crit" actions={<button className={bannerBtn} onClick={() => setActionError(null)}>Fermer</button>}>
          {actionError}
        </Banner>
      )}

      {problem?.status === 'auth-failed' && (
        <Banner
          actions={
            canProvision && (
              <button className={bannerBtn} onClick={() => setModal('edit')}>
                Modifier l'accès
              </button>
            )
          }
        >
          <span className="font-semibold">Identifiants refusés.</span> Le routeur refuse l'utilisateur{' '}
          <span className="font-mono">{mk.username}</span> : mot de passe changé, utilisateur supprimé ou retiré du
          groupe <span className="font-mono">full</span>. Rien n'est collecté tant que l'accès n'est pas corrigé.
        </Banner>
      )}

      {problem?.status === 'hostkey-mismatch' && (
        <Banner
          actions={
            canProvision && (
              <button className={bannerBtn} onClick={repin} disabled={repinning}>
                {repinning ? 'Vérification…' : 'Accepter la nouvelle clé'}
              </button>
            )
          }
        >
          <span className="font-semibold">La clé SSH du routeur a changé.</span> La clé mémorisée à l'ajout (
          <span className="font-mono text-[11px] break-all">{mk.hostKeyFingerprint || '—'}</span>) n'est plus celle
          présentée : routeur réinitialisé ou remplacé, ou connexion interceptée. L'accès est bloqué par prudence. Si le
          changement est attendu, accepter la nouvelle clé.
        </Banner>
      )}

      {problem?.status === 'jump-failed' && (
        <Banner>
          <span className="font-semibold">
            Le serveur de rebond {mk.jumpHostName || ''} est injoignable
          </span>{' '}
          : l'état du routeur est inconnu (aucune alerte hors ligne n'est levée pour lui).
          {problem.detail ? <span className="text-muted"> ({problem.detail})</span> : null}
          {mk.lastPollAt ? <span className="text-muted-2"> Dernière tentative : {fmtDateTime(mk.lastPollAt)}.</span> : null}
        </Banner>
      )}

      {problem && (problem.status === 'unreachable' || problem.status === 'error') && (
        <Banner>
          <span className="font-semibold">{problem.label}.</span> {problem.hint}
          {problem.detail ? <span className="text-muted"> ({problem.detail})</span> : null}
          {mk.lastPollAt ? <span className="text-muted-2"> Dernière tentative : {fmtDateTime(mk.lastPollAt)}.</span> : null}
        </Banner>
      )}

      {upgradeNotice && (
        <Banner tone="info" actions={<button className={bannerBtn} onClick={() => setUpgradeNotice(null)}>Fermer</button>}>
          {upgradeNotice}
        </Banner>
      )}

      {maintenance?.active && (
        <Banner
          tone="info"
          icon={Wrench}
          actions={
            canManageAlerts && (
              <button
                className={bannerBtn}
                onClick={async () => {
                  await api.post('/platform/maintenance-windows/' + maintenance.active.id + '/end', {});
                  loadMaintenance?.();
                }}
              >
                Terminer
              </button>
            )
          }
        >
          <span className="font-semibold">En maintenance</span>
          {" jusqu'à "}
          {maintenance.active.endsAt ? fmtDateTime(maintenance.active.endsAt) : 'arrêt manuel'}
          {' — '}
          {maintenance.active.reason}
        </Banner>
      )}

      {scheduledCmds?.length > 0 && (
        <Banner
          tone="info"
          icon={Clock}
          actions={
            canRun && (
              <button className={bannerBtn} onClick={() => setModal('restart')}>
                Gérer
              </button>
            )
          }
        >
          {scheduledCmds.map((c, i) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 mr-3">
              {mikrotikCommandLabel(c)} planifié le {fmtDateTime(c.scheduledAt)}
              {canRun && (
                <button
                  type="button"
                  onClick={() => cancelScheduled(c.id)}
                  className="bg-transparent border border-rule-3 rounded px-1.5 text-[10px] text-muted cursor-pointer hover:text-crit"
                  title="Annuler cette commande"
                >
                  Annuler
                </button>
              )}
              {i < scheduledCmds.length - 1 ? ' ·' : ''}
            </span>
          ))}
        </Banner>
      )}

      {/* ─── Modales ─── */}
      {modal === 'edit' && (
        <MikrotikDeviceModal
          device={mk}
          onClose={(saved) => {
            setModal(null);
            if (saved) {
              load();
              reload?.();
            }
          }}
        />
      )}
      {modal === 'upgrade' && (
        <MikrotikUpgradeModal
          device={mk}
          label={name}
          onClose={(changed, message) => {
            setModal(null);
            if (changed) {
              if (message) setUpgradeNotice(message);
              loadScheduled?.();
              loadMaintenance?.();
              load();
            }
          }}
        />
      )}
      {modal === 'restart' && (
        <RestartModal
          agentId={agentId}
          label={name}
          isServer
          scheduled={scheduledCmds || []}
          canRun={canRun}
          onClose={(changed) => {
            setModal(null);
            if (changed) {
              loadScheduled?.();
              loadMaintenance?.();
            }
          }}
        />
      )}
      {modal === 'maintenance' && (
        <MaintenanceModal
          agentId={agentId}
          label={name}
          onClose={(changed) => {
            setModal(null);
            if (changed) loadMaintenance?.();
          }}
        />
      )}
      {modal === 'thresholds' && (
        <AlertThresholdsModal
          agentId={agentId}
          computerName={name}
          canEdit={canManageConfig}
          onClose={() => setModal(null)}
        />
      )}

      {/* ─── Sections ─── */}
      <div className="grid grid-cols-2 gap-4 items-start w-full max-[1100px]:grid-cols-1">
        <div>
          <Section icon={Cpu} title="Système">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV label="Modèle" value={mk.model} />
              <KV label="Carte" value={mk.boardName} />
              <KV label="Numéro de série" value={mk.serialNumber} />
              <KV label="Architecture" value={mk.architecture} />
              <KV
                label="Processeur"
                value={
                  [mk.cpuModel, mk.cpuCount ? mk.cpuCount + ' c.' : '', mk.cpuFrequencyMhz ? mk.cpuFrequencyMhz + ' MHz' : '']
                    .filter(Boolean)
                    .join(' · ') || null
                }
              />
              <KV label="CPU (dernière mesure)">{mk.isOnline ? Math.round(mk.cpuPercent) + ' %' : '—'}</KV>
              <KV label="Mémoire">
                {mk.totalMemoryBytes
                  ? fmtBytes(mk.totalMemoryBytes) + (memUsedPct != null ? ` (${memUsedPct} % utilisée)` : '')
                  : '—'}
              </KV>
              <KV label="Stockage">
                {mk.totalHddBytes
                  ? `${fmtBytes(mk.freeHddBytes)} libres / ${fmtBytes(mk.totalHddBytes)}` +
                    (diskUsedPct != null ? ` (${diskUsedPct} % utilisé)` : '')
                  : '—'}
              </KV>
              <KV label="Démarré le" value={mk.lastBootAt ? fmtDateTime(mk.lastBootAt) : null} />
              <KV label="En service depuis" value={mk.lastBootAt ? fmtRelativeFromNow(mk.lastBootAt) : null} />
              <KV label="Température" value={mk.temperatureC != null ? mk.temperatureC + ' °C' : null} />
              <KV label="Tension" value={mk.voltageV != null ? mk.voltageV + ' V' : null} />
              <KV label="Dernière collecte complète" value={mk.lastInventoryAt ? fmtDateTime(mk.lastInventoryAt) : null} />
            </div>
          </Section>

          <Section icon={Tag} title="Version">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV label="RouterOS installé" value={mk.routerOsVersion} />
              <KV label="Canal" value={mk.updateChannel} />
              <KV label="Dernière publiée sur le canal">
                <span className="inline-flex items-center gap-2 flex-wrap">
                  {mk.latestVersion || '—'}
                  {mk.updateAvailable && <UpdatePill>Mise à jour disponible</UpdatePill>}
                </span>
              </KV>
              <KV
                label="Vérifiée le"
                value={mk.latestVersionCheckedAt ? fmtDateTime(mk.latestVersionCheckedAt) : null}
              />
              {mk.isRouterboard && (
                <>
                  <KV label="Firmware actuel" value={mk.currentFirmware} />
                  <KV label="Firmware disponible">
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      {mk.upgradeFirmware || '—'}
                      {mk.firmwareUpgradeAvailable && <UpdatePill>Mise à jour disponible</UpdatePill>}
                    </span>
                  </KV>
                </>
              )}
            </div>
          </Section>

          <Section icon={ShieldCheck} title="Accès">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              <KV label="Hôte" value={mk.host} />
              <KV label="Connexion" value={mk.jumpHostId ? 'Via ' + (mk.jumpHostName || 'serveur de rebond') : 'Directe'} />
              <KV label="Port SSH" value={mk.sshPort} />
              <KV label="Port WinBox" value={mk.winboxPort} />
              <KV label="Utilisateur" value={mk.username} />
              <KV label="Empreinte SSH">
                <span className="text-[11.5px] break-all">
                  {mk.hostKeyAlgorithm ? mk.hostKeyAlgorithm + ' ' : ''}
                  {mk.hostKeyFingerprint || '—'}
                </span>
              </KV>
              <KV label="Clé mémorisée le" value={mk.hostKeyPinnedAt ? fmtDateTime(mk.hostKeyPinnedAt) : null} />
              <KV label="Dernière collecte" value={mk.lastPollAt ? fmtDateTime(mk.lastPollAt) : null} />
              <KV label="Ajouté le" value={fmtDateTime(mk.createdAt)} />
            </div>
          </Section>

          <Section icon={Globe} title={`Adresses IP (${mk.addresses?.length || 0})`}>
            {mk.addresses?.length > 0 ? (
              <table className={tableCls}>
                <thead>
                  <tr>
                    {['Adresse', 'Réseau', 'Interface', ''].map((c, i) => (
                      <th key={i} className={thCls}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mk.addresses.map((ad, i) => (
                    <tr key={i} className={ad.disabled ? 'opacity-50' : ''}>
                      <td className={`${tdCls} font-mono`}>{ad.address}</td>
                      <td className={`${tdCls} font-mono`}>{ad.network}</td>
                      <td className={tdCls}>{ad.interface}</td>
                      <td className={`${tdCls} text-[10px] text-muted-2`}>
                        {[ad.dynamic ? 'dynamique' : '', ad.disabled ? 'désactivée' : ''].filter(Boolean).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-muted-2 text-xs">Aucune adresse collectée.</div>
            )}
          </Section>

          <Section icon={Thermometer} title="Capteurs">
            {mk.health?.length > 0 ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
                {mk.health.map((h, i) => (
                  <KV key={i} label={h.name} value={h.value + (h.unit ? ' ' + (h.unit === 'C' ? '°C' : h.unit) : '')} />
                ))}
              </div>
            ) : (
              <div className="text-muted-2 text-xs">Aucun capteur exposé par ce modèle.</div>
            )}
          </Section>
        </div>

        <div>
          <Section icon={Network} title={`Interfaces (${mk.interfaces?.length || 0})`}>
            {interfaces.length > 0 ? (
              <div className="overflow-x-auto">
                <table className={tableCls}>
                  <thead>
                    <tr>
                      <th className={thCls} {...headerProps('name')}>
                        Nom{arrow('name')}
                      </th>
                      <th className={thCls} {...headerProps('type')}>
                        Type{arrow('type')}
                      </th>
                      <th className={thCls} {...headerProps('state')}>
                        État{arrow('state')}
                      </th>
                      <th className={thCls}>MAC</th>
                      <th className={thCls} {...headerProps('rx')}>
                        Reçu{arrow('rx')}
                      </th>
                      <th className={thCls} {...headerProps('tx')}>
                        Émis{arrow('tx')}
                      </th>
                      <th className={thCls} {...headerProps('downs')} title="Coupures de lien depuis le démarrage">
                        Coupures{arrow('downs')}
                      </th>
                      <th className={thCls}>Commentaire</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interfaces.map((i) => (
                      <tr key={i.name} className={i.disabled ? 'opacity-50' : ''}>
                        <td className={`${tdCls} font-semibold font-mono`}>{i.name}</td>
                        <td className={tdCls}>{i.type}</td>
                        <td className={`${tdCls} whitespace-nowrap`}>
                          {i.disabled ? (
                            <span className="text-muted-2">Désactivée</span>
                          ) : i.running ? (
                            <span className="text-ok">Actif</span>
                          ) : (
                            <span className="text-warn">Sans lien</span>
                          )}
                        </td>
                        <td className={`${tdCls} font-mono text-[11px]`}>{i.macAddress || '—'}</td>
                        <td className={`${tdCls} whitespace-nowrap`}>{fmtBytes(i.rxBytes)}</td>
                        <td className={`${tdCls} whitespace-nowrap`}>{fmtBytes(i.txBytes)}</td>
                        <td className={tdCls} title={i.lastLinkUpTime ? 'Dernier lien établi : ' + i.lastLinkUpTime : undefined}>
                          {i.linkDowns}
                        </td>
                        <td className={`${tdCls} text-muted`}>{i.comment}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted-2 text-xs">Aucune interface collectée.</div>
            )}
          </Section>

          <MikrotikBackupsPanel
            agentId={agentId}
            canDownload={canRemote}
            canRun={canRun}
            sectionCls={sectionCls}
            sTitleCls={sTitleCls}
          />

          {alerts.length > 0 && (
            <Section icon={AlertTriangle} title={`Alertes actives (${alerts.length})`}>
              {[...alerts]
                .sort((x, y) => y.severity - x.severity || new Date(y.occurredAt) - new Date(x.occurredAt))
                .map((al, i) => {
                  const sevColor = ALERT_SEV_COLORS[al.severity] || '#3b82f6';
                  const sevLabel = ALERT_SEV_LABELS[al.severity] || 'Info';
                  const catLabel = ALERT_CAT_LABELS[al.category] || 'Autre';
                  return (
                    <div
                      key={al.id}
                      className={`flex items-center gap-2.5 py-2 ${i < alerts.length - 1 ? 'border-b border-rule' : ''}`}
                    >
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sevColor }} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-fg-2">{al.title}</span>
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                            style={{ background: sevColor + '20', color: sevColor }}
                          >
                            {sevLabel}
                          </span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/10 text-muted">{catLabel}</span>
                        </div>
                        <div className="text-[10px] text-muted-2 mt-0.5">{fmtDateTime(al.occurredAt)}</div>
                      </div>
                      {al.comitBonNumero ? (
                        <ComitBonChip
                          compact
                          numero={al.comitBonNumero}
                          linkedAt={al.comitBonLinkedAt}
                          linkedBy={al.comitBonLinkedBy}
                        />
                      ) : (
                        <ComitTaskButton
                          compact
                          texte={buildAlertTaskText(
                            {
                              ...al,
                              computerName: a.computerName || '',
                              clientName: a.client?.name || '',
                              siteName: a.site?.name || '',
                            },
                            { catLabel, sevLabel }
                          )}
                          comitClientId={a.client?.comitClientId || 0}
                          appareil={a.computerName || ''}
                          cible={{ alertIds: [al.id] }}
                        />
                      )}
                      <button
                        onClick={async () => {
                          await api.post('/platform/alerts/' + al.id + '/resolve');
                          reload?.();
                        }}
                        className="bg-transparent border border-rule-3 rounded-md px-2 py-0.5 text-muted text-[10px] cursor-pointer"
                      >
                        Résoudre
                      </button>
                    </div>
                  );
                })}
            </Section>
          )}

          <DeviceActivityPanel agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />
        </div>
      </div>

      <UptimeHeatmap agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />
      <PerformanceHistory agentId={agentId} sectionCls={sectionCls} sTitleCls={sTitleCls} />
    </div>
  );
}
